
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { parseISO } from "date-fns";
import { calculateBusinessDays, getCurrentYearDateRange } from "@/utils/dateUtils";
import { 
  Profile, 
  VacationRequest, 
  handleQueryResult, 
  handleSingleQueryResult, 
  emptyProfile 
} from "@/utils/supabaseHelpers";

type VacationRequestWithProfile = VacationRequest & {
  profiles: Pick<Profile, "full_name" | "avatar_url">;
};

export const useDashboardData = () => {
  const { data: profile, isLoading: isProfileLoading, isFetched: isProfileFetched } = useQuery({
    queryKey: ["profile"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("No user found");
      
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", user.id)
        .single();
      
      if (error) throw error;
      return handleSingleQueryResult<Profile>(data, emptyProfile);
    },
  });

  const { data: approvedVacations, isLoading: isApprovedLoading } = useQuery({
    queryKey: ["approved_vacations", new Date().getFullYear()],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("No user found");
      
      const { start, end } = getCurrentYearDateRange();
      
      const { data, error } = await supabase
        .from("vacation_requests")
        .select("*")
        .eq("user_id", user.id)
        .eq("status", "approved")
        .gte("start_date", start)
        .lte("start_date", end);
      
      if (error) throw error;
      return handleQueryResult<VacationRequest>(data);
    },
    enabled: !!profile?.id,
  });

  const { data: nextVacation, isLoading: isNextLoading } = useQuery({
    queryKey: ["next_vacation"],
    queryFn: async () => {
      if (!profile?.id) return null;
      
      const today = new Date().toISOString().split('T')[0];
      
      const { data, error } = await supabase
        .from("vacation_requests")
        .select("*")
        .eq("user_id", profile.id)
        .eq("status", "approved")
        .gte("start_date", today)
        .order("start_date")
        .limit(1)
        .maybeSingle();
      
      if (error && error.code !== 'PGRST116') throw error;
      return data as VacationRequest | null;
    },
    enabled: !!profile?.id,
  });

  const { data: currentlyOnVacation, isLoading: isCurrentLoading } = useQuery({
    queryKey: ["currently_on_vacation"],
    queryFn: async () => {
      const today = new Date().toISOString().split('T')[0];
      
      const { data, error } = await supabase
        .from("vacation_requests")
        .select(`
          *,
          profiles!inner (
            full_name,
            avatar_url
          )
        `)
        .eq("status", "approved")
        .lte("start_date", today)
        .gte("end_date", today);

      if (error) throw error;
      
      // Transform the data to match our expected type
      return handleQueryResult<VacationRequestWithProfile>(data);
    },
  });

  // Calculate total approved vacation days
  const approvedDays = approvedVacations?.reduce((total, vacation) => {
    const start = parseISO(vacation.start_date);
    const end = parseISO(vacation.end_date);
    const days = calculateBusinessDays(start, end);
    return total + (vacation.is_half_day ? days * 0.5 : days);
  }, 0) || 0;

  // Calculate remaining vacation days
  const remainingVacationDays = Math.floor(profile?.vacation_days || 0) - Math.floor(approvedDays);

  // Calculate vacation duration if there's a next vacation
  const vacationDuration = nextVacation ? 
    calculateBusinessDays(new Date(nextVacation.start_date), new Date(nextVacation.end_date)) : 0;

  const isLoading = isProfileLoading || (!isProfileFetched) || 
    (!!profile?.id && (isApprovedLoading || isNextLoading)) || isCurrentLoading;

  return {
    profile,
    isProfileLoading: isLoading,
    nextVacation,
    currentlyOnVacation,
    remainingVacationDays,
    vacationDuration,
    isAdmin: profile?.role === 'admin'
  };
};
